import { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, Button, Stack } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';

import { AppContext } from '../context';
import {
    APIGetMyActiveClaim, APIClaimPaymentNumber,
    APIConfirmClaim, APICancelClaim,
    parseApiError,
} from '../api';
import { BANKS, BankBadge, formatPhoneSpaced } from './BankBrand';


export default function PaymentNumberWidget({ claim: externalClaim, onClaimChange }) {
    const { token, showToast } = useContext(AppContext);
    const [internalClaim, setInternalClaim] = useState(null);

    // Если получили claim сверху — используем его (controlled mode)
    const claim = externalClaim !== undefined ? externalClaim : internalClaim;

    const [loading,  setLoading]  = useState(false);
    const [processing, setProcessing] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(null);

    // Раз в 10 клаймов вместо банальной «Отмена» показываем «Галя, у нас отмена».
    // Зафиксировано на id клайма, чтобы текст не прыгал между рендерами.
    const cancelLabel = useMemo(() => {
        if (!claim?.id) return 'Отмена';
        return Math.random() < 0.1 ? 'Галя, у нас отмена' : 'Отмена';
    }, [claim?.id]);

    // Любая правка claim'а одновременно идёт и наверх, и в локальный state
    const setClaim = useCallback((v) => {
        setInternalClaim(v);
        if (onClaimChange) onClaimChange(v);
    }, [onClaimChange]);

    // ── Загрузка текущей активной заявки при монтировании ─────────
    const refresh = useCallback(async () => {
        if (!token) return;
        const data = await APIGetMyActiveClaim(token);
        setClaim(data || null);
    }, [token]);

    useEffect(() => { refresh(); }, [refresh]);

    // Отсчёт от claimed_at + 10 минут
    const [expired, setExpired] = useState(false);

    useEffect(() => {
        if (!claim?.claimed_at) { setSecondsLeft(null); setExpired(false); return; }
        // 10 минут + 3 сек буфер — чтобы клиент гарантированно сообщал «истёк»
        // позже, чем сервер успел переключить статус на expired.
        const expireAt = new Date(claim.claimed_at).getTime() + 10 * 60 * 1000 + 3000;
        let refreshCount = 0;
        const timeouts = [];

        const tick = () => {
            const diff = Math.max(0, Math.floor((expireAt - Date.now()) / 1000));
            setSecondsLeft(diff);
            if (diff === 0 && refreshCount === 0) {
                setExpired(true);          // моментально показываем «истёк»
                refreshCount = 1;
                refresh();                  // первый дёрг — обычно достаточно
                // Подстраховочный повторный refresh через 5 сек на случай,
                // если сервер не успел или сетевой запрос потерялся.
                timeouts.push(setTimeout(() => {
                    refreshCount = 2;
                    refresh();
                }, 5000));
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => {
            clearInterval(id);
            timeouts.forEach(clearTimeout);
        };
    }, [claim?.claimed_at, refresh]);

    // ── Поллинг каждые 30 сек, чтобы реминдер с сервера попадал в UI ──
    useEffect(() => {
        if (!claim) return;
        const interval = setInterval(refresh, 30000);
        return () => clearInterval(interval);
    }, [claim, refresh]);

    // ── Получить номер ────────────────────────────────────────────
    const handleClaim = async () => {
        setLoading(true);
        const res = await APIClaimPaymentNumber(token);
        setLoading(false);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось получить номер'), 'danger');
            return;
        }
        setExpired(false);
        setClaim(res);
    };

    // ── Подтвердить получение оплаты ──────────────────────────────
    const handleConfirm = async () => {
        if (!claim) return;
        setProcessing(true);
        const res = await APIConfirmClaim(token, claim.id);
        setProcessing(false);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось подтвердить'), 'danger');
            return;
        }
        showToast('Оплата подтверждена ✓', 'success');
        setClaim(null);
        setExpired(false);
    };

    // ── Отменить ──────────────────────────────────────────────────
    const handleCancel = async () => {
        if (!claim) return;
        if (!window.confirm(
            'Отменить заявку на этот номер?\n\n' +
            'Используйте, если клиент не платит. ' +
            'Заполненный чек ниже останется на месте — отправите его позже.'
        )) return;
        setProcessing(true);
        const res = await APICancelClaim(token, claim.id);
        setProcessing(false);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось отменить'), 'danger');
            return;
        }
        showToast('Заявка отменена', 'warning');
        setClaim(null);
        setExpired(false);
    };

    // ── Рендер ────────────────────────────────────────────────────
    if (!claim || expired) {
        return (
            <Box>
                {expired && (
                    <Box sx={{
                        mb: 1.2, p: 1.2,
                        borderRadius: 'var(--joy-radius-md)',
                        bgcolor: 'rgba(245,158,11,0.08)',
                        border: '1px solid rgba(245,158,11,0.25)',
                        textAlign: 'center',
                    }}>
                        <Typography sx={{ fontSize: 11, color: '#f59e0b', fontWeight: 700 }}>
                            ⏱ Время вышло — номер вернулся в пул
                        </Typography>
                    </Box>
                )}
                <Button
                    fullWidth
                    size="lg"
                    loading={loading}
                    onClick={handleClaim}
                    startDecorator={<PhoneIphoneRoundedIcon sx={{ fontSize: 20 }} />}
                    sx={{
                        background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                        color: '#fff', fontWeight: 800, fontSize: 14,
                        boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                        py: 1.6,
                        borderRadius: 'var(--joy-radius-xl)',
                        '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                    }}
                >
                    {expired ? 'Получить новый номер' : 'Получить оплату'}
                </Button>
            </Box>
        );
    }

    // У нас есть активная заявка — показываем большую карточку с номером
    const pn = claim.payment_number_detail || {};
    const bankMeta = BANKS[pn.bank] || BANKS.other;

    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={claim.id}
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            >
                {/* Главная карточка с номером */}
                <Box sx={{
                    background: `linear-gradient(135deg, ${bankMeta.color} 0%, ${bankMeta.color}cc 100%)`,
                    borderRadius: 'var(--joy-radius-xl)',
                    p: 2.5,
                    boxShadow: `0 12px 40px ${bankMeta.color}55`,
                    border: '2px solid rgba(255,255,255,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                }}>
                    {/* Тонкое свечение в углу */}
                    <Box sx={{
                        position: 'absolute',
                        top: -40, right: -40,
                        width: 140, height: 140,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.07)',
                        filter: 'blur(20px)',
                        pointerEvents: 'none',
                    }} />

                    <Box sx={{ position: 'relative', textAlign: 'center', mb: 1 }}>
                        <Typography sx={{
                            fontSize: 11, fontWeight: 700,
                            color: bankMeta.textColor, opacity: 0.75,
                            textTransform: 'uppercase', letterSpacing: '0.1em',
                            mb: 0.8,
                        }}>
                            💸 Номер для перевода 💸
                        </Typography>
                        <Typography sx={{
                            fontSize: { xs: 18, sm: 22 },
                            fontWeight: 900,
                            color: bankMeta.textColor,
                            fontFamily: '"SF Mono", "JetBrains Mono", Menlo, monospace',
                            letterSpacing: '-0.01em',
                            fontVariantNumeric: 'tabular-nums',
                        }}>
                            {formatPhoneSpaced(pn.phone)}
                        </Typography>
                    </Box>

                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 1, mt: 1.2,
                        pt: 1.2, borderTop: '1px solid rgba(255,255,255,0.15)',
                    }}>
                        <BankBadge bank={pn.bank} size={26} />
                        <Typography sx={{
                            fontSize: 14, fontWeight: 800,
                            color: bankMeta.textColor,
                        }}>
                            {bankMeta.name} <span style={{ opacity: 0.7 }}>· {pn.owner_name}</span>
                        </Typography>
                    </Box>
                </Box>

                {/* Подсказка фотографу */}
                {/* <Box sx={{
                    mt: 1.2, p: 1.2,
                    borderRadius: 'var(--joy-radius-md)',
                    bgcolor: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.25)',
                    textAlign: 'center',
                }}>
                    <Typography sx={{ fontSize: 13, color: '#22c55e', fontWeight: 700 }}>
                        💡 Заполните чек ниже и нажмите «Подтвердить оплату»
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                        Номер автоматически привяжется к чеку
                    </Typography>
                </Box> */}

                {/* «Отмена» — на случай если клиент передумал.
                    Раз в 10 раз пасхалкой вылазит «Галя, у нас отмена» 🤓 */}
                <Button
                    fullWidth
                    variant="soft"
                    color="danger"
                    loading={processing}
                    onClick={handleCancel}
                    startDecorator={<CancelRoundedIcon sx={{ fontSize: 18 }} />}
                    sx={{ mt: 1, fontWeight: 700 }}
                >
                    {cancelLabel}
                </Button>

                {secondsLeft !== null && (
                    <Typography sx={{
                        fontSize: 10,
                        color: secondsLeft < 120 ? '#f87171' : secondsLeft < 300 ? '#f59e0b' : 'var(--joy-palette-text-tertiary)',
                        textAlign: 'center', mt: 1, fontWeight: 600,
                    }}>
                        ⏱ Осталось: {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
                    </Typography>
                )}
            </motion.div>
        </AnimatePresence>
    );
}